import {
  formatDuration,
  tashkentHour,
  type Lang,
  type Me,
  type StoriesResponse,
  type StoryListItem,
} from '@fonus/shared';
import type { MessageKey } from '@fonus/shared/i18n';
import { usePlayer } from '../audio/engine.ts';
import type { T } from '../i18n/useT.ts';
import { usePlayItem } from '../lib/actions.ts';
import { continueItem, isLocked } from '../lib/catalog.ts';
import { useUi } from '../state/ui.ts';
import { haptic } from '../telegram/tg.ts';
import { Chip, PlayPill, SegmentedControl } from '../ui/controls.tsx';
import { Cover } from '../ui/Cover.tsx';
import { PersonIcon, PlayIcon } from '../ui/icons.tsx';
import {
  minutesLabel,
  quotaChip,
  StoryRow,
  storyStyles as ss,
  type RowState,
} from '../ui/story.tsx';
import s from './home.module.css';

export function greetingKey(hour: number): MessageKey {
  if (hour >= 5 && hour < 12) return 'greeting.morning';
  if (hour >= 12 && hour < 18) return 'greeting.day';
  if (hour >= 18 && hour < 22) return 'greeting.evening';
  return 'greeting.night';
}

export function Header({ name, t }: { name: string; t: T }) {
  const open = useUi((st) => st.open);
  return (
    <header className={s.header}>
      <div className={s.headerText}>
        <span className="t-subhead secondary ellipsis">
          {t(greetingKey(tashkentHour(new Date())), { name })}
        </span>
        <h1 className="t-large">{t('home.title')}</h1>
      </div>
      <button
        type="button"
        className={s.profileBtn}
        aria-label={t('home.profile')}
        onClick={() => {
          open({ kind: 'profile' });
        }}
      >
        <PersonIcon />
      </button>
    </header>
  );
}

export function LanguageSwitch({
  lang,
  onChange,
  t,
}: {
  lang: Lang;
  onChange: (l: Lang) => void;
  t: T;
}) {
  return (
    <SegmentedControl
      label={t('home.language')}
      value={lang}
      onChange={(l) => {
        haptic.select();
        onChange(l);
      }}
      options={(['uz', 'ru', 'en'] as const).map((l) => ({ value: l, label: t(`lang.${l}`) }))}
    />
  );
}

function rowState(
  item: StoryListItem,
  data: StoriesResponse,
  playingId: string | undefined,
): RowState {
  if (item.isDraft) return 'draft';
  if (item.id === playingId) return 'playing';
  if (isLocked(item, data)) return 'locked';
  if (item.listened) return 'listened';
  return 'default';
}

export function Home({
  me,
  data,
  lang,
  onLangChange,
  t,
}: {
  me: Pick<Me, 'firstName'>;
  data: StoriesResponse;
  lang: Lang;
  onLangChange: (l: Lang) => void;
  t: T;
}) {
  const play = usePlayItem(lang);
  const playingId = usePlayer((st) => st.story?.id);
  const status = usePlayer((st) => st.status);
  const tonight = data.items.find((i) => i.isTonight) ?? data.items.find((i) => !i.isDraft);
  const cont = playingId ? undefined : continueItem(data.items);
  const chip = quotaChip(data, t);
  const isPlaying = status === 'playing' || status === 'buffering';

  return (
    <main className={s.screen}>
      <Header name={me.firstName} t={t} />
      <LanguageSwitch lang={lang} onChange={onLangChange} t={t} />

      {tonight && (
        <article className={ss.featured} aria-labelledby="tonight-title">
          <Cover
            hue={tonight.hue}
            size={334}
            radius={20}
            style={{ width: '100%', height: 'auto', aspectRatio: '4 / 3' }}
          />
          <div className={ss.featuredText}>
            <div className={ss.featuredTop}>
              <span className="t-footnote accent-text" style={{ fontWeight: 700 }}>
                {t('home.tonight')} · {minutesLabel(tonight.durationSec, t)}
              </span>
              <Chip tone={chip.tone}>{chip.text}</Chip>
            </div>
            <h2 id="tonight-title" className="t-title2">
              {tonight.title}
            </h2>
            <p className="t-subhead secondary ellipsis" style={{ fontWeight: 500 }}>
              {tonight.summary}
            </p>
          </div>
          <div className={ss.featuredActions}>
            <PlayPill
              label={t('home.listen')}
              onClick={() => {
                play(tonight);
              }}
            />
          </div>
        </article>
      )}

      {cont && (
        <section className={s.section}>
          <h3 className={`t-section ${s.sectionTitle}`}>{t('home.continue')}</h3>
          <button
            type="button"
            className={ss.continue}
            onClick={() => {
              play(cont);
            }}
          >
            <Cover hue={cont.hue} size={56} radius={14} />
            <span
              style={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}
            >
              <span className="t-headline ellipsis">{cont.title}</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className={ss.progressTrack}>
                  <span
                    className={ss.progressFill}
                    style={{
                      display: 'block',
                      width: `${Math.min(100, (cont.progressSec / Math.max(1, cont.durationSec ?? 1)) * 100)}%`,
                    }}
                  />
                </span>
                <span className="t-footnote secondary" style={{ whiteSpace: 'nowrap' }}>
                  {t('home.minutesLeft', {
                    count: Math.max(
                      1,
                      Math.round(((cont.durationSec ?? 0) - cont.progressSec) / 60),
                    ),
                  })}
                </span>
              </span>
            </span>
            <span
              className={`${ss.badge} ${ss.badgePlay}`}
              style={{ width: 40, height: 40 }}
              aria-hidden="true"
            >
              <PlayIcon size={18} />
            </span>
          </button>
        </section>
      )}

      <section className={s.section}>
        <h3 className={`t-section ${s.sectionTitle}`}>{t('home.all')}</h3>
        <div className={ss.group}>
          {data.items.map((item) => {
            const state = rowState(item, data, playingId);
            const meta =
              state === 'playing' ? (
                <PlayingTime fallback={item.durationSec ?? 0} />
              ) : (
                minutesLabel(item.durationSec, t)
              );
            return (
              <StoryRow
                key={item.id}
                title={item.title}
                hue={item.hue}
                meta={meta}
                state={state}
                isPlaying={isPlaying}
                t={t}
                onClick={() => {
                  play(item);
                }}
              />
            );
          })}
        </div>
      </section>
    </main>
  );
}

/** Only this tiny node re-renders on every timeupdate, not the whole list. */
function PlayingTime({ fallback }: { fallback: number }) {
  const position = usePlayer((st) => st.position);
  const duration = usePlayer((st) => st.duration);
  return (
    <>
      {formatDuration(position)} / {formatDuration(duration || fallback)}
    </>
  );
}

export function HomeSkeleton() {
  return (
    <main className={s.screen} aria-busy="true">
      <header className={s.header}>
        <div className={s.headerText} style={{ gap: 8 }}>
          <div className={s.sk} style={{ width: 150, height: 14, borderRadius: 7 }} />
          <div className={s.sk} style={{ width: 160, height: 34, borderRadius: 10 }} />
        </div>
        <div className={s.sk} style={{ width: 44, height: 44, borderRadius: 999 }} />
      </header>
      <div className={s.sk} style={{ height: 44, borderRadius: 999 }} />
      <div className={s.skCard}>
        <div className={s.sk2} style={{ aspectRatio: '4 / 3', borderRadius: 20 }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '0 6px 6px' }}>
          <div className={s.sk} style={{ width: 140, height: 12, borderRadius: 6 }} />
          <div
            className={s.sk}
            style={{ width: 230, maxWidth: '80%', height: 20, borderRadius: 10 }}
          />
          <div
            className={s.sk}
            style={{ width: 290, maxWidth: '95%', height: 12, borderRadius: 6 }}
          />
          <div
            className={s.sk}
            style={{ width: 128, height: 48, borderRadius: 999, marginTop: 6 }}
          />
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div className={s.sk} style={{ width: 150, height: 18, borderRadius: 9, marginLeft: 4 }} />
        <div className={s.skList}>
          {['62%', '48%', '70%'].map((w) => (
            <div key={w} className={s.skRow}>
              <div
                className={s.sk2}
                style={{ width: 56, height: 56, flexShrink: 0, borderRadius: 14 }}
              />
              <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div className={s.sk} style={{ height: 14, borderRadius: 7, width: w }} />
                <div className={s.sk} style={{ width: 56, height: 12, borderRadius: 6 }} />
              </div>
              <div
                className={s.sk}
                style={{ width: 36, height: 36, borderRadius: 999, marginRight: 6 }}
              />
            </div>
          ))}
        </div>
      </div>
    </main>
  );
}
